import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { ProcessingStatusWidget } from './ProcessingStatusWidget.js';

const DEMO_MODE = import.meta.env.VITE_SFP_DEMO_MODE === 'true';
const DEMO_QUEUE = [
  { id: 'doc_a17b9c', label: 'Side Letter — Topco3 LP', status: 'IN PROCESS', href: '/documents/doc_a17b9c' },
  { id: 'doc_b42f01', label: 'PCAP_FUND_IV_LTD Q1', status: 'FINAL REVIEW', href: '/documents/doc_b42f01' },
  { id: 'doc_c08e22', label: 'Con Ed — March statement', status: 'IN PROCESS', href: '/documents/doc_c08e22' },
];
const DEMO_RECENT = [
  { id: 'doc_a17b9c', name: 'Topco3 Side Letter — executed.pdf', category: 'fund_admin' },
  { id: 'doc_b42f01', name: 'PCAP_FUND_IV_LTD_Q1.xlsx', category: 'fund_accounting' },
  { id: 'doc_c08e22', name: 'Con Ed — March 2026.pdf', category: 'bill_pay' },
  { id: 'doc_d99a44', name: 'Topco LP K-1 (2025).pdf', category: 'tax' },
  { id: 'doc_e55c11', name: 'Trust amendment — exec copy.pdf', category: 'legal_personal' },
];
import type {
  Widget,
  ActionRequiredWidget,
  QueueWidget,
  NoteWidget,
  LinksWidget,
  MetricWidget,
  RecentDocumentsWidget,
  BadgeSource,
} from '../../lib/home-schema.js';

interface WidgetHostProps {
  widget: Widget;
  counts: Record<BadgeSource, number>;
  editing?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
}

export function WidgetCard({ widget, counts, editing, onEdit, onRemove }: WidgetHostProps) {
  return (
    <div className="relative rounded-2xl border border-stone-200/60 bg-white p-4 shadow-sm">
      {editing && (
        <div className="absolute right-2 top-2 flex gap-1">
          <button
            onClick={onEdit}
            className="rounded bg-stone-200 px-2 py-0.5 text-[10px] text-stone-700 hover:bg-stone-300"
            type="button"
          >
            Edit
          </button>
          <button
            onClick={onRemove}
            className="rounded bg-[#9C4A3A] px-2 py-0.5 text-[10px] text-white hover:bg-[#7E3A2D]"
            type="button"
          >
            Remove
          </button>
        </div>
      )}
      <WidgetBody widget={widget} counts={counts} />
    </div>
  );
}

function WidgetBody({ widget, counts }: { widget: Widget; counts: Record<BadgeSource, number> }) {
  switch (widget.kind) {
    case 'action_required':
      return <ActionRequiredCard w={widget} counts={counts} />;
    case 'queue':
      return <QueueCard w={widget} />;
    case 'note':
      return <NoteCard w={widget} />;
    case 'links':
      return <LinksCard w={widget} />;
    case 'metric':
      return <MetricCard w={widget} counts={counts} />;
    case 'recent_documents':
      return <RecentDocumentsCard w={widget} />;
    case 'processing_status':
      return <ProcessingStatusWidget title={widget.title} />;
  }
}

function ActionRequiredCard({ w, counts }: { w: ActionRequiredWidget; counts: Record<BadgeSource, number> }) {
  const triage = counts.triage;
  const past = counts.bills_past_due;
  return (
    <div>
      <div className="flex items-start justify-between">
        <div className="text-sm font-semibold text-stone-800">{w.title}</div>
        <Link to={w.inboxHref} className="text-[10px] uppercase tracking-wider text-stone-400 hover:text-stone-700">
          Inbox →
        </Link>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <Stat label={w.primaryLabel ?? 'Triage'} value={triage} accent="amber" />
        <Stat label={w.secondaryLabel ?? 'Past due'} value={past} accent="terra" />
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent: 'amber' | 'terra' }) {
  const ring = accent === 'amber' ? 'bg-[#C97A2E]' : 'bg-[#9C4A3A]';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-400">{label}</div>
      <div className={`mt-1 inline-grid h-7 min-w-[28px] place-items-center rounded-full px-2 text-xs font-semibold text-white ${ring}`}>
        {value}
      </div>
    </div>
  );
}

function QueueCard({ w }: { w: QueueWidget }) {
  const [items, setItems] = useState<Array<{ id: string; label: string; status: string; href: string }>>(
    w.source === 'static' ? w.staticItems ?? [] : DEMO_MODE ? DEMO_QUEUE.slice(0, w.limit) : [],
  );

  useEffect(() => {
    if (w.source === 'static') return;
    if (DEMO_MODE) {
      setItems(DEMO_QUEUE.slice(0, w.limit));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let q;
        if (w.source === 'triage') {
          q = query(
            collection(db, 'documents'),
            where('status', '==', 'triage'),
            orderBy('uploadedAt', 'desc'),
            limit(w.limit),
          );
        } else if (w.source === 'bills_due') {
          q = query(
            collection(db, 'bills'),
            where('status', 'in', ['received', 'review', 'approved', 'scheduled']),
            orderBy('dueDate', 'asc'),
            limit(w.limit),
          );
        } else {
          q = query(
            collection(db, 'bills'),
            where('status', 'in', ['received', 'review', 'approved', 'scheduled']),
            orderBy('dueDate', 'asc'),
            limit(w.limit),
          );
        }
        const snap = await getDocs(q);
        if (cancelled) return;
        setItems(
          snap.docs.map((d) => {
            const data = d.data() as any;
            const label =
              data.originalName ?? data.biller?.name ?? data.summary?.slice(0, 64) ?? d.id;
            const href = w.source === 'triage' ? `/documents/${d.id}` : `/bills/${d.id}`;
            return { id: d.id, label, status: data.status, href };
          }),
        );
      } catch {
        /* leave items as-is */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [w.source, w.limit]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-stone-800">{w.title}</div>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">{items.length}</div>
      </div>
      <div className="mt-3 space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-stone-400 italic">Nothing in the queue.</div>
        )}
        {items.map((it) => (
          <Link
            key={it.id}
            to={it.href}
            className="block rounded-lg border border-stone-200/80 bg-stone-50 px-3 py-2 text-xs hover:bg-stone-100"
          >
            <div className="text-[10px] uppercase tracking-wider text-[#2F4F3D]">{it.id.slice(0, 8)}</div>
            <div className="mt-0.5 text-[10px] font-semibold uppercase text-stone-500">{it.status}</div>
            <div className="mt-1 line-clamp-2 text-stone-700">{it.label}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function NoteCard({ w }: { w: NoteWidget }) {
  return (
    <div>
      <div className="text-sm font-semibold text-stone-800">{w.title}</div>
      <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-stone-600">{w.body}</p>
    </div>
  );
}

function LinksCard({ w }: { w: LinksWidget }) {
  return (
    <div>
      <div className="text-sm font-semibold text-stone-800">{w.title}</div>
      <ul className="mt-2 space-y-1">
        {w.links.map((l) =>
          l.external ? (
            <li key={l.id}>
              <a className="text-xs text-[color:var(--sfp-primary)] hover:underline" href={l.href} target="_blank" rel="noreferrer">
                {l.label} ↗
              </a>
            </li>
          ) : (
            <li key={l.id}>
              <Link className="text-xs text-[color:var(--sfp-primary)] hover:underline" to={l.href}>
                {l.label}
              </Link>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

function MetricCard({ w, counts }: { w: MetricWidget; counts: Record<BadgeSource, number> }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-400">{w.title}</div>
      <div className="mt-1 text-2xl font-semibold text-stone-800">{counts[w.source] ?? 0}</div>
      {w.caption && <div className="mt-1 text-xs text-stone-500">{w.caption}</div>}
    </div>
  );
}

function RecentDocumentsCard({ w }: { w: RecentDocumentsWidget }) {
  const [items, setItems] = useState<Array<{ id: string; name: string; category: string }>>(
    DEMO_MODE ? DEMO_RECENT.slice(0, w.limit) : [],
  );
  useEffect(() => {
    if (DEMO_MODE) {
      setItems(DEMO_RECENT.slice(0, w.limit));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const q = query(collection(db, 'documents'), orderBy('uploadedAt', 'desc'), limit(w.limit));
        const snap = await getDocs(q);
        if (cancelled) return;
        setItems(
          snap.docs.map((d) => {
            const data = d.data() as any;
            return {
              id: d.id,
              name: data.originalName ?? d.id,
              category: data.category ?? '—',
            };
          }),
        );
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [w.limit]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-stone-800">{w.title}</div>
        <Link to="/documents" className="text-[10px] uppercase tracking-wider text-stone-400 hover:text-stone-700">
          All →
        </Link>
      </div>
      <div className="mt-2 space-y-1.5">
        {items.length === 0 && (
          <div className="text-xs text-stone-400 italic">No documents yet.</div>
        )}
        {items.map((it) => (
          <Link
            key={it.id}
            to={`/documents/${it.id}`}
            className="block truncate text-xs text-stone-700 hover:text-stone-900"
            title={it.name}
          >
            <span className="mr-1.5 text-[10px] uppercase text-stone-400">{it.category}</span>
            {it.name}
          </Link>
        ))}
      </div>
    </div>
  );
}
